import React from 'react';

import {
  AddressIcon,
  BankIcon,
  DollarIcon,
  NoteIcon,
  HashTagIcon,
  ScheduleNotSetIcon,
} from '@sharedCR/coreDesignSystem/icons';

import { closingLocationOptions } from 'constants/options';
import { StatusColor, StatusEnum } from 'constants/status';
import { getConditionalArray, getValueOrDash } from 'utils/data';

import { ClosingInfoCardId, FundingInfoCardId } from './LoanClosingCDS.constants';
import { formatCurrency, formatDate, formatDateTime } from './LoanClosingCDS.utils';

export const getClosingInformationCardsConfig = ({
  closingInfo,
  isTwoPartClosing = false,
  isUnsecured = false,
}) => {
  const locationName = closingLocationOptions.find(
    ({ value }) => value === closingInfo?.location,
  )?.name;

  return [
    {
      id: ClosingInfoCardId.EARLIEST_CLOSING_DATE,
      pendingChangesKey: 'anticipatedClosingDate',
      name: 'Anticipated Closing Date',
      value: formatDate(closingInfo?.anticipatedClosingDate),
      icon: <ScheduleNotSetIcon />,
      iconColor: StatusColor[StatusEnum.INFO],
    },
    ...getConditionalArray(isTwoPartClosing && !isUnsecured, () => [
      {
        id: ClosingInfoCardId.FIRST_CLOSING_DATE,
        pendingChangesKey: 'firstCloseAt',
        name: 'First Closing Date',
        value: formatDateTime(closingInfo?.firstCloseAt),
        icon: <ScheduleNotSetIcon />,
        iconColor: StatusColor[StatusEnum.INFO],
      },
    ]),
    {
      id: ClosingInfoCardId.CLOSING_DATE,
      pendingChangesKey: 'closeAt',
      name: isTwoPartClosing && !isUnsecured ? 'Second Closing Date' : 'Closing Date',
      value: formatDateTime(closingInfo?.closeAt),
      icon: <ScheduleNotSetIcon />,
      iconColor: StatusColor[StatusEnum.INFO],
    },
    ...getConditionalArray(!isUnsecured, () => [
      {
        id: ClosingInfoCardId.CLOSING_LOCATION,
        pendingChangesKey: 'location',
        name: 'Closing Location',
        value: getValueOrDash(locationName),
        icon: <AddressIcon />,
        iconColor: StatusColor[StatusEnum.ERROR],
      },
    ]),
  ];
};

export const getFundingInformationCardsConfig = ({
  fundingInfo,
  warehouseBankOptions,
  isUnsecured = false,
}) => {
  const warehouseBankName = getValueOrDash(
    warehouseBankOptions?.find(({ value }) => value === fundingInfo?.fundingInfoWarehouseBankId)
      ?.name,
  );

  return [
    {
      id: FundingInfoCardId.FUNDING_TYPE,
      pendingChangesKey: 'type',
      name: 'Funding Type',
      value: getValueOrDash(fundingInfo?.type),
      icon: <NoteIcon />,
      iconColor: StatusColor[StatusEnum.INFO],
    },
    {
      id: FundingInfoCardId.FUNDING_NUMBER,
      pendingChangesKey: 'fundingNumber',
      name: 'Funding Number',
      value: getValueOrDash(fundingInfo?.fundingNumber),
      icon: <HashTagIcon />,
      iconColor: StatusColor[StatusEnum.INFO],
    },
    {
      id: FundingInfoCardId.WIRE_AMOUNT,
      pendingChangesKey: 'fundingInfoWireAmount',
      name: 'Wire Amount',
      value: formatCurrency(fundingInfo?.fundingInfoWireAmount),
      icon: <DollarIcon />,
      iconColor: StatusColor[StatusEnum.INFO],
    },
    ...getConditionalArray(!isUnsecured, () => [
      {
        id: FundingInfoCardId.ADVANCE_AMOUNT,
        pendingChangesKey: 'advanceAmount',
        name: 'Advance Amount',
        value: formatCurrency(fundingInfo?.advanceAmount),
        icon: <DollarIcon />,
        iconColor: StatusColor[StatusEnum.WARNING],
      },
      {
        id: FundingInfoCardId.WAREHOUSE_BANK,
        pendingChangesKey: 'fundingInfoWarehouseBankId',
        name: 'Warehouse Bank',
        value: warehouseBankName,
        icon: <BankIcon />,
        iconColor: StatusColor[StatusEnum.SUCCESS],
      },
    ]),
  ];
};

export const getClosingDocumentsConfig = closingDocuments => {
  if (!closingDocuments?.length) {
    return { lastGenerated: '-', documentName: null, documentUrl: null };
  }

  const latestDoc = closingDocuments[0];

  return {
    lastGenerated: formatDate(latestDoc?.createdAt),
    documentName: latestDoc?.name ?? null,
    documentUrl: latestDoc?.url ?? null,
  };
};
